import React, { useEffect, useRef, useState } from "react";
import { PiMapPinLineFill } from "react-icons/pi";
import { IoWifiOutline } from "react-icons/io5";
import { dataStyle, mapStyles } from "../../../components/Map/GoogleMapStyle";

interface LocationProps {
  lat: number;
  lng: number;
  className?: string;
  forestData?: any;
}

export const Location: React.FC<LocationProps> = ({
  lat,
  lng,
  className,
  forestData,
}: LocationProps) => {
  const [map, setMap] = useState<google.maps.Map | undefined>();
  const ref = useRef<HTMLDivElement>(null);
  const [isSatelliteView, setIsSatelliteView] = useState<boolean>(false);
  const markerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (ref.current && !map) {
      const initializedMap = new window.google.maps.Map(ref.current, {
        zoom: 6,
        center: {
          lat: lat,
          lng: lng,
        },
        restriction: {
          latLngBounds: { north: 90, south: -90, east: 180, west: -180 },
          strictBounds: false,
        },
        styles: mapStyles,
        streetViewControl: false,
        mapTypeControl: false,
      });

      const marker = new window.google.maps.Marker({
        position: {
          lat: lat,
          lng: lng,
        },
        map: initializedMap,
        title: "test",
        icon: {
          url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
        },
      });

      markerRef.current = marker;
      setMap(initializedMap);
    }
  }, [ref, map, lat, lng]);

  // Update map center and marker when lat/lng change
  useEffect(() => {
    if (map && markerRef.current) {
      const newPosition = { lat, lng };
      map.setCenter(newPosition);
      markerRef.current.setPosition(newPosition);
    }
  }, [map, lat, lng]);

  useEffect(() => {
    let features: google.maps.Data.Feature[] = [];
    if (ref.current && map && forestData) {
      features = map.data.addGeoJson(forestData);
      map.data.setStyle(dataStyle);
    }

    return () => {
      features.forEach((feature) => map?.data.remove(feature));
    };
  }, [map, forestData]);

  useEffect(() => {
    if (map) {
      map.setMapTypeId(isSatelliteView ? "satellite" : "roadmap");
    }
  }, [map, isSatelliteView]);

  return (
    <div className={`h-full w-full flex-1 ${className}`}>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-lg font-medium">Location</h3>
        <div className="my-3 inline-flex divide-x">
          <button
            className={
              "rounded-l-full border border-[#dfdfdf] px-3 py-1 text-sm font-medium" +
              (isSatelliteView ? " bg-whie text-black" : " bg-[#e6e6e6] text-purple-800")
            }
            onClick={() => setIsSatelliteView(false)}
          >
            <span className="inline-flex items-center gap-2">
              <PiMapPinLineFill />
              Map
            </span>
          </button>
          <button
            className={
              "rounded-r-full border border-[#dfdfdf] px-3 py-1 text-sm font-medium" +
              (isSatelliteView ? " bg-[#e6e6e6] text-purple-800" : " bg-whie text-black")
            }
            onClick={() => setIsSatelliteView(true)}
          >
            <span className="inline-flex items-center gap-2">
              <IoWifiOutline />
              Satellite
            </span>
          </button>
        </div>
      </div>
      <div className="h-96 w-full">
        <div ref={ref} id="map" style={{ width: "100%", height: "100%", borderRadius: "5px" }} />
      </div>
    </div>
  );
};
